import { JSX, useState } from "react";

export interface Slice {
  label: string;
  value: number;
}

const PALETTE = [
  "#EA33C0",
  "#7C3AED",
  "#2563EB",
  "#0EA5E9",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#14B8A6",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#22C55E",
];

/**
 * Dependency-free SVG donut chart with an interactive legend.
 * Clicking a legend item hides that slice; the donut and percentages
 * recompute from the remaining visible slices.
 */
export default function DonutChart({
  data,
  size = 230,
  thickness = 30,
  emptyText = "No data yet.",
}: {
  data: Slice[];
  size?: number;
  thickness?: number;
  emptyText?: string;
}): JSX.Element {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (label: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  const colorOf = (i: number) => PALETTE[i % PALETTE.length];

  const visible = data.filter((d) => !hidden.has(d.label) && d.value > 0);
  const total = visible.reduce((sum, d) => sum + d.value, 0);

  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * r;

  // build arc segments from visible slices
  let cumulative = 0;
  const segments = visible.map((d) => {
    const frac = total > 0 ? d.value / total : 0;
    const dash = frac * C;
    const seg = {
      label: d.label,
      color: colorOf(data.findIndex((x) => x.label === d.label)),
      dash,
      offset: -cumulative,
      pct: Math.round(frac * 100),
    };
    cumulative += dash;
    return seg;
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#F1F5F9"
              strokeWidth={thickness}
            />
            {segments.map((s) => (
              <circle
                key={s.label}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${s.dash} ${C - s.dash}`}
                strokeDashoffset={s.offset}
                style={{ transition: "stroke-dasharray 0.4s, stroke-dashoffset 0.4s" }}
              />
            ))}
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-semibold text-black leading-none">
            {total.toLocaleString()}
          </span>
          <span className="text-xs text-gray-500 mt-1">Total</span>
        </div>
      </div>

      {/* interactive legend */}
      <div className="flex flex-col gap-2 w-full">
        {data.length === 0 && (
          <p className="text-sm text-gray-400">{emptyText}</p>
        )}
        {data.map((d, i) => {
          const isHidden = hidden.has(d.label);
          const pct =
            total > 0 && !isHidden ? Math.round((d.value / total) * 100) : 0;
          return (
            <button
              key={d.label}
              onClick={() => toggle(d.label)}
              className={`flex items-center gap-2 text-sm text-left transition-opacity ${
                isHidden ? "opacity-40" : "hover:opacity-80"
              }`}
              title={isHidden ? "Click to show" : "Click to hide"}
            >
              <span
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: colorOf(i) }}
              />
              <span
                className={`flex-1 truncate ${isHidden ? "line-through" : ""}`}
              >
                {d.label}
              </span>
              <span className="text-gray-500 tabular-nums">
                {d.value}
                {!isHidden && d.value > 0 && (
                  <span className="text-gray-400"> · {pct}%</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
